import { useEffect, useState } from "react";
import { useLocale } from "react-aria-components";
import { useComponentsTranslation } from "../i18n/i18next.ts";
import { formatElapsedTime, formatRelativeTime, isPastRelativeTimeCutoff } from "./dates.ts";

const TICK_INTERVAL_MS = 60 * 1000;

function useMinuteTick(
    date: string | Date | number | undefined,
    shouldStop: (date: string | Date | number) => boolean
) {
    const [, forceRender] = useState(0);

    useEffect(() => {
        if (!date || shouldStop(date)) {
            return;
        }

        const intervalId = setInterval(() => {
            forceRender(tick => tick + 1);

            if (shouldStop(date)) {
                clearInterval(intervalId);
            }
        }, TICK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [date, shouldStop]);
}

const neverStop = () => false;

// Re-renders every minute so the formatted output advances (e.g. "just now" -> "1 minute ago"),
// until the label switches to an absolute time (see isPastRelativeTimeCutoff), at which point it stops ticking.
export function useRelativeTime(date: string | Date | number | undefined): string {
    const { locale } = useLocale();
    const { t } = useComponentsTranslation();
    useMinuteTick(date, isPastRelativeTimeCutoff);

    return date ? formatRelativeTime(date, locale, t) : "";
}

// Re-renders every minute for as long as it is mounted, since the label never stops advancing.
export function useElapsedTime(date: string | Date | number | undefined): string {
    const { locale } = useLocale();
    const { t } = useComponentsTranslation();
    useMinuteTick(date, neverStop);

    return date ? formatElapsedTime(date, locale, t) : "";
}
